import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

/**
 * Canonical 12 Coop leaf Policies (TM-3756).
 * Same vocabulary as TipMi violatedPolicies, Coop Overview chips, and creator notify.
 * Other is human-only and is listed separately on Reject (not a scored vector).
 */
export const COOP_LEAF_POLICIES = [
  'Child Safety',
  'Violence / Threats',
  'Harassment / Bullying',
  'Hate Speech',
  'Sexual / Explicit Content',
  'Privacy & Doxxing',
  'Self-Harm & Suicide',
  'Spam',
  'Impersonation / Fake Accounts',
  'Illicit Substances',
  'Weapons & Dangerous Goods',
  'Copyright / Intellectual Property',
] as const;

export const OTHER_POLICY = 'Other';

/** Reject checklist: 12 Coop Policies + human-only Other. */
export const REJECT_POLICIES = [...COOP_LEAF_POLICIES, OTHER_POLICY] as const;

/**
 * Automated Policy list for thresholds / chips (12 only — no Other).
 * @deprecated Prefer {@link COOP_LEAF_POLICIES}; kept for existing imports.
 */
export const PENALTY_NOTIFICATION_POLICIES = COOP_LEAF_POLICIES;

/** @deprecated Prefer COOP_LEAF_POLICIES / REJECT_POLICIES. */
export const HARD_GATE_POLICIES = REJECT_POLICIES;

export type CoopLeafPolicy = (typeof COOP_LEAF_POLICIES)[number];
export type PenaltyNotificationPolicy = CoopLeafPolicy | typeof OTHER_POLICY;
export type HardGatePolicy = PenaltyNotificationPolicy;

export interface RejectWithPoliciesResult {
  confirmed: boolean;
  violatedPolicies: string[];
  notes?: string;
}

interface RejectWithPoliciesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending?: boolean;
  onConfirm: (result: Omit<RejectWithPoliciesResult, 'confirmed'>) => void | Promise<void>;
  /** Override dialog title (e.g. bulk reject with count). */
  title?: string;
  /** Override body copy above the policy checklist. */
  description?: string;
}

/**
 * Human Review Reject: require ≥1 of the 12 Coop Policies (or Other); Other requires a description.
 */
export function RejectWithPoliciesDialog({
  open,
  onOpenChange,
  pending = false,
  onConfirm,
  title = 'Reject Content',
  description =
    'Select which Community Guidelines Policies this content violated. The post will leave the review queue and be hidden from users.',
}: RejectWithPoliciesDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setNotes('');
    }
  }, [open]);

  function toggle(policy: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(policy);
      else next.delete(policy);
      return next;
    });
  }

  const otherSelected = selected.has(OTHER_POLICY);
  const notesRequired = otherSelected;
  const notesOk = !notesRequired || notes.trim().length > 0;
  const canConfirm = selected.size > 0 && notesOk && !pending;

  async function handleConfirm() {
    if (!canConfirm) return;
    const violatedPolicies = REJECT_POLICIES.filter((p) => selected.has(p));
    const trimmed = notes.trim();
    await onConfirm({
      violatedPolicies,
      ...(trimmed ? { notes: trimmed } : {}),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Policies violated</p>
          <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
            {REJECT_POLICIES.map((policy) => {
              const checked = selected.has(policy);
              return (
                <label
                  key={policy}
                  className="flex cursor-pointer items-start gap-2 text-sm"
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={checked}
                    onCheckedChange={(v) => toggle(policy, v === true)}
                    disabled={pending}
                    aria-label={policy}
                  />
                  <span>{policy}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            Notes {notesRequired ? '(required for Other)' : '(optional)'}
          </p>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              notesRequired
                ? 'Describe the Other policy violation…'
                : 'Add moderator notes…'
            }
            disabled={pending}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={() => void handleConfirm()} disabled={!canConfirm}>
            {pending ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
